import { usePublishedData } from "../lib/published-data";
import { createFileRoute, notFound, redirect, useLocation } from "@tanstack/react-router";
import membershipStyles from "../styles/membership.css?url";
import { DocumentContent } from "../components/document-content";
import { DemoJoin, JoinContent } from "../components/membership/join-content";
import { creatorPageHead, shellFrom, withStylesheet } from "../lib/head";
import { useShell } from "../lib/document-route";
import { keepSearch, searchText } from "../lib/search";
import { loadJoin } from "../server/membership";

/**
 * Membership (/join): the page the creator composed, with the plans at its built-in block; the
 * landing for a no-charge test checkout; or the native page. A course or content checkout
 * (?course=, ?content=) always shows the native page.
 */
export const Route = createFileRoute("/join/")({
  validateSearch: keepSearch,
  loaderDeps: ({ search }) => ({
    course: searchText(search, "course"),
    content: searchText(search, "content"),
    preview: search.preview !== undefined,
  }),
  loader: async ({ deps }) => {
    const data = await loadJoin({ data: deps });
    if (data.kind === "missing") throw notFound();
    if (data.kind === "redirect") throw redirect({ href: data.to, statusCode: 302 });
    return data;
  },
  head: ({ matches, loaderData }) => {
    const shell = shellFrom(matches);
    if (!shell || !loaderData) return {};
    const head =
      loaderData.kind === "page"
        ? creatorPageHead(shell, {
            pathname: "/join",
            title: loaderData.page.seoTitle || loaderData.page.title,
            description: loaderData.page.description,
            canonicalPath: loaderData.page.path,
            noindex: loaderData.private || loaderData.locked || loaderData.page.hideFromSearch,
            preloadImage: loaderData.lead,
          })
        : creatorPageHead(shell, {
            pathname: "/join",
            title: loaderData.title,
            description: loaderData.description,
          });
    return withStylesheet(head, membershipStyles);
  },
  component: Membership,
});

function Membership() {
  const initial = Route.useLoaderData();
  const shell = useShell();
  const { data } = usePublishedData(initial, shell, "/join", "page");
  const location = useLocation();
  if (data.kind === "page")
    return (
      <DocumentContent
        data={data}
        shell={shell}
        builtIn={
          data.builtIn ? (
            <JoinContent view={data.builtIn} ownerMemberships={shell.owner?.memberships} />
          ) : undefined
        }
      />
    );
  if (data.kind === "demo")
    return (
      <DemoJoin
        creatorName={data.creatorName}
        browsePath={data.browsePath}
        href={location.pathname + location.searchStr}
        stripe={shell.demoCheckout?.stripe ?? false}
      />
    );
  // Each checkout (a course, a post) starts on its own first billing term.
  return (
    <JoinContent
      key={location.searchStr}
      view={data.content}
      ownerMemberships={shell.owner?.memberships}
    />
  );
}
